/**
 * ExemptionsReviewPanel (Sprint M5)
 *
 * Exemptions-specific HITL review surfaced inside the Data Validation stage when a document carries
 * Exemptions enrichment (homestead verification + exemption-fraud scoring).
 *
 * The clerk confirms or overrides each result; overrides are written through the existing
 * ValidationContext as edits to the synthetic fields (homesteadStatus / exemptionRiskLevel) the
 * backend wrote into the document field set. Because those fields are real entries in
 * `state.document.fields`, edits are tracked in the edit history AND captured server-side by
 * persistFieldCorrections (FIELD_CORRECTIONS_PERSISTED audit entry) on submit — no separate
 * persistence endpoint. Status is always conveyed as text + an icon (never color alone) and every
 * control has a visible, associated label. Mirrors DeedsReviewPanel — net-new framework code = 0.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useValidation } from '../../contexts/ValidationContext';

// ── Types (frontend mirror of azure-functions/src/types/exemptions.ts) ─────────

type HomesteadStatus = 'verified' | 'needs-review' | 'failed';
type RiskLevel = 'low' | 'medium' | 'high';

interface HomesteadCheck {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
}
interface Homestead {
  status: HomesteadStatus;
  checks: HomesteadCheck[];
  requiresReview: boolean;
}
interface TriggeredSignal {
  id: string;
  label: string;
  weight: number;
  source: 'rule' | 'llm';
  rationale?: string;
}
interface Fraud {
  score: number;
  riskLevel: RiskLevel;
  triggered: TriggeredSignal[];
  rationale: string;
  requiresReview: boolean;
}
export interface ExemptionsEnrichment {
  homestead?: Homestead;
  fraud?: Fraud;
  requiresReview: boolean;
}

// Synthetic field ids — must match EXEMPTIONS_SYNTHETIC_FIELDS in the backend (`field-{name}`).
const FIELD_ID = {
  homesteadStatus: 'field-homesteadStatus',
  riskLevel: 'field-exemptionRiskLevel'
};

const HOMESTEAD_OPTIONS: Array<{ value: HomesteadStatus; label: string }> = [
  { value: 'verified', label: 'Verified' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'failed', label: 'Failed' }
];
const RISK_OPTIONS: Array<{ value: RiskLevel; label: string }> = [
  { value: 'low', label: 'Low risk' },
  { value: 'medium', label: 'Medium risk' },
  { value: 'high', label: 'High risk' }
];

/** Status chip with text + icon (never color alone) for a homestead-verification outcome. */
function HomesteadStatusChip({ status }: { status: HomesteadStatus }) {
  const map: Record<HomesteadStatus, { label: string; color: 'success' | 'warning' | 'error'; icon: React.ReactElement }> = {
    verified: { label: 'Verified', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    'needs-review': { label: 'Needs review', color: 'warning', icon: <WarningAmberIcon fontSize="small" /> },
    failed: { label: 'Failed', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> }
  };
  const m = map[status];
  return <Chip size="small" variant="outlined" color={m.color} icon={m.icon} label={m.label} />;
}

/** Risk chip with text + icon (never color alone) for the fraud risk level. */
function RiskLevelChip({ level }: { level: RiskLevel }) {
  const map: Record<RiskLevel, { label: string; color: 'success' | 'warning' | 'error'; icon: React.ReactElement }> = {
    low: { label: 'Low risk', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    medium: { label: 'Medium risk', color: 'warning', icon: <WarningAmberIcon fontSize="small" /> },
    high: { label: 'High risk', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> }
  };
  const m = map[level];
  return <Chip size="small" variant="outlined" color={m.color} icon={m.icon} label={m.label} />;
}

interface ExemptionsReviewPanelProps {
  exemptions: ExemptionsEnrichment;
}

const ExemptionsReviewPanel: React.FC<ExemptionsReviewPanelProps> = ({ exemptions }) => {
  const { state, actions } = useValidation();
  const { homestead, fraud } = exemptions;

  const homesteadValue = (state.validatedFields[FIELD_ID.homesteadStatus] ?? homestead?.status ?? '') as HomesteadStatus | '';
  const riskValue = (state.validatedFields[FIELD_ID.riskLevel] ?? fraud?.riskLevel ?? '') as RiskLevel | '';

  if (!homestead && !fraud) return null;

  return (
    <Box sx={{ mb: 3 }} aria-label="Exemptions review">
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Exemptions Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Confirm the homestead verification and exemption-fraud risk before approval.
      </Typography>

      <Stack spacing={2}>
        {/* ── Homestead verification ── */}
        {homestead && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                  Homestead Verification
                </Typography>
                <HomesteadStatusChip status={homestead.status} />
              </Box>

              {homestead.checks.length > 0 && (
                <List dense disablePadding sx={{ mb: 2 }}>
                  {homestead.checks.map((c) => (
                    <ListItem key={c.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        {c.passed
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <WarningAmberIcon fontSize="small" color="warning" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={`${c.label}: ${c.passed ? 'Pass' : 'Review'}`}
                        secondary={c.detail}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <FormControl fullWidth>
                <InputLabel id="homestead-status-label">Homestead status</InputLabel>
                <Select
                  labelId="homestead-status-label"
                  label="Homestead status"
                  value={homesteadValue}
                  onChange={(e) => actions.updateField(FIELD_ID.homesteadStatus, e.target.value)}
                >
                  {HOMESTEAD_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {/* ── Exemption-fraud scoring ── */}
        {fraud && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                  Exemption Fraud Risk
                </Typography>
                <RiskLevelChip level={fraud.riskLevel} />
                <Chip size="small" variant="outlined" label={`Score ${fraud.score}/100`} />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 32 }}>
                {fraud.triggered.length === 0 ? (
                  <Chip size="small" variant="outlined" icon={<CheckCircleIcon fontSize="small" />} label="No fraud signals" />
                ) : (
                  fraud.triggered.map((t) => {
                    const chip = (
                      <Chip key={t.id} size="small" color="warning" variant="outlined"
                        icon={<WarningAmberIcon fontSize="small" />}
                        label={`Flagged: ${t.label}${t.source === 'llm' ? ' (AI)' : ''}`} />
                    );
                    return t.rationale
                      ? <Tooltip key={t.id} title={t.rationale} arrow>{chip}</Tooltip>
                      : chip;
                  })
                )}
              </Box>

              <FormControl fullWidth>
                <InputLabel id="risk-level-label">Risk level</InputLabel>
                <Select
                  labelId="risk-level-label"
                  label="Risk level"
                  value={riskValue}
                  onChange={(e) => actions.updateField(FIELD_ID.riskLevel, e.target.value)}
                >
                  {RISK_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default ExemptionsReviewPanel;
